import React, { useState } from "react";

const Section = ({ id, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="mb-4" aria-labelledby={id}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-white px-6 py-4 rounded-lg shadow-sm hover:shadow-md transition"
        aria-expanded={open}
        aria-controls={`${id}-content`}
      >
        <h3 id={id} className="text-lg font-semibold text-[#0a363c]">
          {title}
        </h3>
        <span className="text-[#0a363c]/70">{open ? "−" : "+"}</span>
      </button>

      <div
        id={`${id}-content`}
        className={`mt-3 px-6 text-[#0a363c]/85 bg-white rounded-b-lg transition-all ${
          open ? "max-h-none py-6 overflow-visible" : "max-h-0 overflow-hidden"
        }`}
        style={{ transitionDuration: "250ms" }}
      >
        {children}
      </div>
    </section>
  );
};


const TermsAndConditions = ({
  lastUpdated = "December 16, 2025",
  showAccept = false,
  onAccept = null,
}) => {
  const [accepted, setAccepted] = useState(false);

  const handleAccept = () => {
    if (!accepted) {
      alert("Please tick the checkbox to accept the Terms of Service.");
      return;
    }
    if (onAccept) onAccept();
    else alert("Thank you — Terms accepted.");
  };

  const handlePrint = () => {
    const printContents =
      document.getElementById("printable-terms")?.innerHTML || "";
    const printWindow = window.open("", "", "height=800,width=900");

    printWindow.document.write(`
      <html>
        <head>
          <title>Terms of Service – CashCanvas</title>
          <style>
            body { font-family: Arial, sans-serif; color: #0a363c; background: white; line-height: 1.6; }
            h1, h2, h3 { color: #0a363c; }
            section { margin-bottom: 2rem; page-break-inside: avoid; }
            .no-print { display: none !important; }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  return (
    <main className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <header className="bg-white p-8 rounded-2xl shadow-md mb-8 no-print">
          <div className="flex items-start justify-between gap-6">
            <p className="text-sm text-[#0a363c]/70">
              Last updated:{" "}
              <span className="font-medium text-[#0a363c]">{lastUpdated}</span>
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrint}
                className="px-4 py-2 rounded-full bg-[#e2bf9c] text-[#0a363c] font-semibold shadow hover:opacity-95 transition"
              >
                Print
              </button>
              <button
                onClick={handlePrint}
                className="px-4 py-2 rounded-full border border-[#e2bf9c] text-[#0a363c] font-semibold hover:bg-[#e2bf9c]/10 transition"
              >
                Download (PDF)
              </button>
            </div>
          </div>
        </header>

        {/* Terms Content */}
        <div id="printable-terms">
          <div className="space-y-6">
            <Section id="intro" title="Introduction" defaultOpen>
              <p className="mb-3">Terms of Service</p>
              <p className="mb-3">Last Updated: December 16, 2025</p>
              <p className="mb-3">
                Welcome to CashCanvas, operated by EmoyenTECH SAS, located in
                Mulhouse, France (“Company”, “we”, “our”, or “us”). These Terms of
                Service ("Terms") govern your use of the CashCanvas platform,
                website, application and related services (collectively, the
                “Service”).
              </p>
              <p className="mb-3">
                By accessing or using the Service, you agree to be bound by these
                Terms in Full. If you do not agree, do not use the Service.
              </p>
              <p className="text-sm text-[#0a363c]/70">
                If you are accepting these Terms on behalf of an organization, you
                represent that you have authority to bind that organization.
              </p>
            </Section>

            <Section id="eligibility" title="1. Eligibility">
              <p className="mb-3">
                You must be at least 13 years old to use the Service. If you are
                under 18 (or the age of majority in your country), you must have
                the permission and supervision of a parent or legal guardian.
              </p>
              <p className="mb-3">
                By using the Service, you represent and warrant that:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>You are eligible to use the Service;</li>
                <li>You will provide accurate information;</li>
                <li>Your use complies with applicable laws.</li>
              </ul>
            </Section>

            <Section id="account" title="2. Your Account">
              <p className="mb-3">
                To access certain features, you must create an account. You are
                responsible for maintaining the confidentiality of your account
                credentials and for all activities under your account.
              </p>
              <p>
                We may suspend or terminate your account if we reasonably suspect
                unauthorized use or violation of these Terms.
              </p>
            </Section>

            <Section id="payments" title="3. Subscriptions and Payments">
              <p className="mb-3">
                CashCanvas offers a freemium model with optional paid tiers.
                Subscriptions are processed securely via Stripe. Pricing and
                features are described on our website.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>All fees are in USD or your local currency.</li>
                <li>You may cancel your subscription at any time via your account settings.</li>
                <li>No refunds are issued for partial subscription periods unless required by law.</li>
                <li>Failure to pay may result in account suspension or loss of access to premium features.</li>
              </ul>
            </Section>

            <Section id="acceptable-use" title="4. Acceptable Use">
              <p className="mb-3">You agree not to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Use the Service for unlawful purposes;</li>
                <li>Copy, resell, or exploit the Service;</li>
                <li>Upload or transmit malicious code or harmful content;</li>
                <li>Interfere with or disrupt the Service or servers;</li>
                <li>Use automated scripts to scrape or interact with the Service without permission.</li>
              </ul>
              <p className="mt-3">
                We reserve the right to suspend or terminate accounts engaging in
                abusive or prohibited behavior.
              </p>
            </Section>

            <Section id="ip" title="5. Intellectual Property">
              <p>
                All content, logos, and technology in the Service are the property
                of EmoyenTECH SAS or our licensors and are protected by
                intellectual property laws.
              </p>
              <p>
                You may not copy, modify, distribute, or create derivative works
                from our content or software unless we give you permission.
              </p>
            </Section>

            <Section id="services" title="6. Services">
              <p>
                We provide tools to track, visualize and categorize financial
                activity. We may add, remove or change features at our discretion.
                Access may be provided via web, mobile apps or third-party
                integrations (e.g., AI APIs) which are subject to their own terms
                and privacy notices.
              </p>
            </Section>

            <Section id="thirdparty" title="7. Third-Party Services">
              <p className="mb-3">
                CashCanvas integrates third-party services and APIs, including but
                not limited to:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>OpenAI (for AI-powered insights)</li>
                <li>Firebase Auth (for authentication)</li>
                <li>Google AdSense (for advertising)</li>
                <li>Financial market APIs (for currency, stock, fund, and crypto data)</li>
              </ul>
              <p className="mt-3">
                These services may collect and use data according to their own
                policies. We are not responsible for the privacy practices or
                actions of third-party providers.
              </p>
            </Section>

            <Section id="disclaimer" title="8. Disclaimer of Warranties">
              <p className="mb-3">
                The Service is provided "as is" and "as available", without
                warranties of any kind, express or implied. We do not guarantee the
                accuracy, reliability, or availability of the Service.
              </p>
              <p>
                You use the Service at your own risk. We do not offer financial or
                legal or insurance or tax advice. All insights are for educational
                or informational purposes only and should not be considered
                recommendations.
              </p>
            </Section>

            <Section id="privacy" title="9. Privacy & Data">
              <p className="mb-3">
                Our Privacy Policy describes how we collect, use, store and share
                your information. As an EU-based company, we are subject to the
                General Data Protection Regulation (GDPR). By using the Service, you
                consent to the processing of your data, including transfers to
                third-party providers (such as OpenAI) located outside the EEA.
                These cross-border transfers are secured via Standard Contractual
                Clauses (SCCs) or other appropriate legal safeguards under GDPR
                Chapter V.
              </p>
            </Section>

            <Section id="liability" title="10. Limitation of Liability">
              <p className="mb-3">
                To the fullest extent permitted by law, EmoyenTECH SAS shall not be
                liable for any indirect, incidental, special, or consequential
                damages, including loss of data, profits, or business opportunities
                arising out of your use of the Service.
              </p>
              <p>
                This limitation shall not apply to damages caused by our willful
                misconduct (dol) or gross negligence (faute lourde). Our total
                liability for any claim shall not exceed the greater of 100USD or
                the total amount you paid to us in the past 12 months.
              </p>
            </Section>

            <Section id="indemnity" title="11. Indemnity">
              <p>
                You agree to indemnify and hold harmless EmoyenTECH SAS, its
                officers, employees and agents from claims arising from your misuse
                of the Service, violation of these Terms, or infringement by your
                User Data.
              </p>
            </Section>

            <Section id="termination" title="12. Termination">
              <p>
                We may suspend or terminate your access at any time if you violate
                these Terms or if we discontinue the Service. Upon termination,
                your right to use the Service ends immediately.
              </p>
            </Section>

            <Section id="changes" title="13. Changes to the Terms">
              <p>
                We may update these Terms from time to time. When we do, we will
                revise the "Last Updated" date. If changes are significant, we may
                notify you by email or within the app.
              </p>
              <p className="mt-2">
                Continued use of the Service after the update means you accept the
                revised Terms.
              </p>
            </Section>

            <Section id="law" title="14. Governing Law and Disputes">
              <p>
                These Terms are governed by the laws of France, without regard to
                conflict of law principles. Any disputes will be resolved in the
                courts of Mulhouse, France, unless otherwise required by applicable
                consumer laws.
              </p>
            </Section>

            <Section id="contact" title="15. Contact">
              <p className="mb-3">
                For questions, legal notices, or concerns, please contact:
              </p>
              <address className="not-italic space-y-1">
                <div>EmoyenTECH SAS</div>
                <div>68 rue de la couronne</div>
                <div>68400 Riedishiem</div>
                <div>
                  Email:{" "}
                  <a
                    href="mailto:support@mycashcanvas.com"
                    className="underline"
                  >
                    support@mycashcanvas.com
                  </a>
                </div>
              </address>
            </Section>

            <Section id="misc" title="16. Miscellaneous">
              <p>
                If any provision of these Terms is held invalid, the remaining
                provisions will remain in full force. These Terms constitute the
                entire agreement between you and EmoyenTECH SAS regarding the
                Service.
              </p>
            </Section>

            {showAccept && (
              <div className="bg-white p-6 rounded-lg shadow-md mt-6 flex items-center justify-between gap-4 no-print">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    className="h-5 w-5"
                  />
                  <span>I agree to the Terms of Service</span>
                </label>
                <button
                  onClick={handleAccept}
                  disabled={!accepted}
                  className={`px-6 py-2 rounded-full font-semibold text-white ${
                    accepted ? "bg-[#0a363c]" : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  Accept
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default TermsAndConditions;
